import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import { BASE_URL } from "../../const";

function authHeader() {
  const t = localStorage.getItem("token");
  if (!t) return {};
  return { Authorization: t.startsWith("Bearer ") || t.startsWith("Token ") ? t : `Token ${t}` };
}

// ── constants ────────────────────────────────────────────────────────────────

const VENDOR_STATUSES = ["pending", "open", "vendor_confirmed", "partially_dispatched", "fully_dispatched", "cancelled"];

const VENDOR_STATUS_COLORS = {
  pending:              { bg: "#fff7ed", text: "#c2410c", border: "#fed7aa" },
  open:                 { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
  vendor_confirmed:     { bg: "#f0fdf4", text: "#166534", border: "#bbf7d0" },
  partially_dispatched: { bg: "#fefce8", text: "#854d0e", border: "#fef08a" },
  fully_dispatched:     { bg: "#f0fdf4", text: "#166534", border: "#bbf7d0" },
  cancelled:            { bg: "#fef2f2", text: "#dc2626", border: "#fecaca" },
};

// ── shared styles ────────────────────────────────────────────────────────────

const inputStyle = { width: "100%", padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, color: "#111827", outline: "none", boxSizing: "border-box", background: "#fff" };
const btnGhost   = { padding: "7px 12px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", fontSize: 12, color: "#374151", cursor: "pointer", whiteSpace: "nowrap" };
const btnPrimary = { padding: "8px 16px", background: "#7c3aed", border: "none", borderRadius: 8, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" };

// ── badges ───────────────────────────────────────────────────────────────────

function VendorStatusBadge({ value }) {
  const c = VENDOR_STATUS_COLORS[value] || { bg: "#f8fafc", text: "#64748b", border: "#e2e8f0" };
  return (
    <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600, textTransform: "capitalize", whiteSpace: "nowrap" }}>
      {(value || "—").replace(/_/g, " ")}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Requirements list (starting point — "Start Invoicing" navigates to InvoiceProcessing)
// ════════════════════════════════════════════════════════════════════════════

export default function InvoicingDetails() {
  const navigate = useNavigate();

  const [rows, setRows]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [pageSize]              = useState(100);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [filters, setFilters]   = useState({
    search: "", sku: "", vendor_status: "",
    vendor_status_not: "fully_dispatched",
  });

  const fetchData = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (filters.search)            params.set("search",            filters.search);
      if (filters.sku)               params.set("sku",               filters.sku);
      if (filters.vendor_status)     params.set("vendor_status",     filters.vendor_status);
      if (filters.vendor_status_not) params.set("vendor_status_not", filters.vendor_status_not);
      const res  = await fetch(`${BASE_URL}/vendor/requirements/?${params}`, { headers: authHeader() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to load");
      setRows(data?.results || data?.data?.results || data?.data || []);
      setTotal(data?.count || data?.data?.count || 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [page, pageSize, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Group rows by request_code
  const groupKeys = [];
  const groupMap  = {};
  for (const row of rows) {
    if (!groupMap[row.request_code]) {
      groupMap[row.request_code] = [];
      groupKeys.push(row.request_code);
    }
    groupMap[row.request_code].push(row);
  }

  const totalPages = Math.ceil(total / pageSize) || 1;

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      <style>{`
        @media (max-width: 640px) {
          .inv-wrap { padding: 14px 12px !important; }
          .inv-filters { padding: 10px 12px !important; }
          .inv-filters input, .inv-filters select { width: 100% !important; }
          .inv-count { margin-left: 0 !important; }
        }
      `}</style>
      <Navbar />
      <div className="inv-wrap" style={{ maxWidth: 1400, margin: "0 auto", padding: "24px 20px" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111827", margin: 0 }}>Invoicing Details</h1>
            <p style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>Select a requirement to start or continue invoice processing</p>
          </div>
        </div>

        {/* Filters */}
        <div className="inv-filters" style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", padding: "14px 16px", marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            placeholder="Search code, SKU, vendor…"
            value={filters.search}
            onChange={e => { setFilters(p => ({ ...p, search: e.target.value })); setPage(1); }}
            style={{ ...inputStyle, width: 220 }}
          />
          <input
            placeholder="SKU"
            value={filters.sku}
            onChange={e => { setFilters(p => ({ ...p, sku: e.target.value })); setPage(1); }}
            style={{ ...inputStyle, width: 140 }}
          />
          <select
            value={filters.vendor_status}
            onChange={e => {
              const val = e.target.value === "__all__" ? "" : e.target.value;
              setFilters(p => ({
                ...p,
                vendor_status: val,
                // clear the exclusion filter when user picks any specific status or show-all
                vendor_status_not: val || e.target.value === "__all__" ? "" : "fully_dispatched",
              }));
              setPage(1);
            }}
            style={{ ...inputStyle, width: 190 }}
          >
            <option value="">All except Fully Dispatched</option>
            <option value="__all__">— Show All —</option>
            {VENDOR_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <button
            onClick={() => { setFilters({ search: "", sku: "", vendor_status: "", vendor_status_not: "fully_dispatched" }); setPage(1); }}
            style={btnGhost}
          >
            Reset
          </button>
          <span className="inv-count" style={{ marginLeft: "auto", fontSize: 12, color: "#9ca3af" }}>{groupKeys.length} requirements · {total} rows</span>
        </div>

        {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", color: "#dc2626", fontSize: 13, marginBottom: 12 }}>{error}</div>}

        {/* Requirements table */}
        <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  {["Created Date", "Req Code", "SKUs", "Total Qty", "Vendor", "Delivery Party", "Vendor Status", "Tracking", "Action"].map(h => (
                    <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 600, color: "#374151", whiteSpace: "nowrap", fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} style={{ textAlign: "center", padding: 48, color: "#9ca3af" }}>Loading…</td></tr>
                ) : groupKeys.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign: "center", padding: 48, color: "#9ca3af" }}>No active requirements found</td></tr>
                ) : groupKeys.map((code, gi) => {
                  const group    = groupMap[code];
                  const first    = group[0];
                  const totalQty = group.reduce((s, r) => s + (Number(r.required_qty) || 0), 0);
                  const extra    = group.length - 1;
                  return (
                    <tr
                      key={code}
                      style={{
                        borderBottom: "1px solid #f1f5f9",
                        background: gi % 2 === 0 ? "#fff" : "#fafafa",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        navigate("/purchase/invoicing/process", {
                          state: { requirementGroup: group },
                        })
                      }
                      onMouseEnter={e => (e.currentTarget.style.background = "#faf5ff")}
                      onMouseLeave={e =>
                        (e.currentTarget.style.background =
                          gi % 2 === 0 ? "#fff" : "#fafafa")
                      }
                    >
                      <td
                        style={{
                          padding: "12px 16px",
                          color: "#6b7280",
                          fontSize: 12,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {first.requirement_date || "—"}
                      </td>

                      <td
                        style={{
                          padding: "12px 16px",
                          fontFamily: "monospace",
                          fontWeight: 700,
                          color: "#111827",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {code}
                      </td>

                      <td style={{ padding: "12px 16px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            flexWrap: "wrap",
                          }}
                        >
                          <span style={{ fontWeight: 500, color: "#111827" }}>
                            {first.sku_name || first.sku || "—"}
                          </span>

                          {extra > 0 && (
                            <span
                              style={{
                                background: "#f1f5f9",
                                color: "#6b7280",
                                borderRadius: 12,
                                padding: "1px 7px",
                                fontSize: 11,
                                fontWeight: 600,
                              }}
                            >
                              +{extra} more
                            </span>
                          )}
                        </div>
                      </td>

                      <td
                        style={{
                          padding: "12px 16px",
                          fontWeight: 700,
                          color: "#111827",
                          textAlign: "right",
                        }}
                      >
                        {totalQty}
                      </td>

                      <td
                        style={{
                          padding: "12px 16px",
                          color: "#374151",
                          maxWidth: 160,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {first.vendor_name || "—"}
                      </td>

                      <td
                        style={{
                          padding: "12px 16px",
                          color: "#374151",
                          maxWidth: 140,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {first.delivery_party_name || "—"}
                      </td>

                      <td style={{ padding: "12px 16px" }}>
                        <VendorStatusBadge value={first.vendor_status} />
                      </td>

                      <td style={{ padding: "12px 16px" }}>
                        <span style={{ fontSize: 11, color: "#6b7280" }}>
                          {(first.tracking_status || "—").replace(/_/g, " ")}
                        </span>
                      </td>

                      <td style={{ padding: "12px 16px" }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();

                            navigate("/purchase/invoicing/process", {
                              state: { requirementGroup: group },
                            });
                          }}
                          style={{ ...btnPrimary, padding: "6px 14px", fontSize: 12 }}
                        >
                          Start Invoicing →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderTop: "1px solid #f1f5f9" }}>
            <span style={{ fontSize: 12, color: "#9ca3af" }}>Page {page} of {totalPages} · {total} total rows</span>
            <div style={{ display: "flex", gap: 6 }}>
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ ...btnGhost, cursor: page <= 1 ? "default" : "pointer" }}>← Prev</button>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ ...btnGhost, cursor: page >= totalPages ? "default" : "pointer" }}>Next →</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
